/**
 * BESTIX v5.0 — Category Card
 * Photo-based category browse cards
 */

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

export interface CategoryCardProps {
  name: string;
  eventCount: number;
  imageUrl: string;
  href: string;
  description?: string;
  variant?: 'default' | 'large' | 'horizontal';
}

// ============================================
// Category Card Component
// ============================================

export function CategoryCard({
  name,
  eventCount,
  imageUrl,
  href,
  description,
  variant = 'default',
}: CategoryCardProps) {
  return (
    <Link
      to={href}
      className={cn(
        'group relative block overflow-hidden rounded-xl transition-all duration-300 hover:shadow-xl',
        variant === 'horizontal' ? 'flex flex-row h-48' : variant === 'large' ? 'aspect-[3/4]' : 'aspect-[4/5]'
      )}
    >
      <div className={cn(
        'absolute inset-0',
        variant === 'horizontal' && 'relative w-48 flex-shrink-0'
      )}>
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className={cn(
          'absolute inset-0',
          variant === 'horizontal'
            ? 'bg-gradient-to-r from-[var(--color-navy)]/60 to-transparent'
            : 'bg-gradient-to-t from-[var(--color-navy)]/80 via-[var(--color-navy)]/40 to-transparent'
        )} />
      </div>

      <div className={cn(
        variant === 'horizontal'
          ? 'relative flex-1 p-6 flex flex-col justify-center bg-white'
          : 'absolute bottom-0 left-0 right-0 p-5'
      )}>
        <h3 className={cn(
          'font-playfair font-medium',
          variant === 'horizontal' ? 'text-2xl text-[var(--color-navy)]' : 'text-xl text-white !text-white'
        )}>
          {name}
        </h3>
        <p className={cn('mt-1', variant === 'horizontal' ? 'text-gray-500' : 'text-white/70')}>
          {eventCount} {eventCount === 1 ? 'event' : 'events'}
        </p>
        {description && (
          <p className={cn(
            'mt-2 text-sm line-clamp-2',
            variant === 'horizontal' ? 'text-gray-600' : 'text-white/80'
          )}>
            {description}
          </p>
        )}
        <div className={cn(
          'flex items-center gap-1 mt-3 text-sm font-medium transition-colors',
          variant === 'horizontal' ? 'text-[var(--color-gold)] hover:text-[var(--color-gold-dark)]' : 'text-white hover:text-[var(--color-gold)]'
        )}>
          Explore
          <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}

// ============================================
// Category Card Grid
// ============================================

interface CategoryCardGridProps {
  categories: CategoryCardProps[];
  layout?: 'equal' | 'featured' | 'list';
  className?: string;
}

export function CategoryCardGrid({ categories, layout = 'equal', className }: CategoryCardGridProps) {
  if (layout === 'list') {
    return (
      <div className={cn('space-y-4', className)}>
        {categories.map((category) => (
          <CategoryCard key={category.name} {...category} variant="horizontal" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6', className)}>
      {categories.map((category) => (
        <CategoryCard key={category.name} {...category} />
      ))}
    </div>
  );
}

export default CategoryCard;
